<template>
	<div id="phonetrack_prefs" class="section">
		<h2>
			<PhonetrackIcon />
			<span>PhoneTrack</span>
		</h2>
		<div id="phonetrack-content">
			<div class="line">
				<NcInputField
					id="phonetrack-quota"
					v-model="state.pointQuota"
					class="input"
					type="number"
					:label="t('phonetrack', 'Point number quota')"
					:show-trailing-button="!!state.pointQuota"
					@update:model-value="onQuotaUpdate()"
					@trailing-button-click="state.pointQuota = '' ; onQuotaUpdate()">
					<template #icon>
						<TimerAlertOutlineIcon :size="20" />
					</template>
					<template #trailing-button-icon>
						<CloseIcon :size="20" />
					</template>
				</NcInputField>
				<NcButton variant="tertiary"
					:title="t('phonetrack', 'The maximum number of points each user can store/log.')
						+ '\n' + t('phonetrack', 'Each user can choose what happens when the quota is reached : block logging or delete oldest point.')
						+ '\n' + t('phonetrack', 'An empty value means no limit.')">
					<template #icon>
						<HelpCircleOutlineIcon />
					</template>
				</NcButton>
			</div>
			<div>
				<NcNoteCard v-if="state.maptiler_api_key === ''" type="warning">
					<span v-html="mainHintHtml" />
				</NcNoteCard>
				<NcNoteCard type="info">
					{{ t('phonetrack', 'The API key defined here will be used by all users. Each user can set a personal API key to override this global one.') }}
				</NcNoteCard>
			</div>
			<NcTextField
				v-model="state.maptiler_api_key"
				:label="t('phonetrack', 'Maptiler API key')"
				type="password"
				class="input"
				:placeholder="t('phonetrack', 'my-api-key')"
				:show-trailing-button="!!state.maptiler_api_key"
				@update:model-value="onInput"
				@trailing-button-click="state.maptiler_api_key = ''; onInput()">
				<template #icon>
					<KeyIcon :size="20" />
				</template>
			</NcTextField>
			<NcFormBox>
				<NcFormBoxSwitch :model-value="state.proxy_osm"
					@update:model-value="onCheckboxChanged($event, 'proxy_osm')">
					<div class="checkbox-inner">
						<ArrowDecisionOutlineIcon :size="20" class="inline-icon" />
						{{ t('phonetrack', 'Proxy map tiles/vectors requests via Nextcloud') }}
					</div>
				</NcFormBoxSwitch>
			</NcFormBox>
			<TileServerList
				class="admin-tile-server-list"
				:tile-servers="state.extra_tile_servers"
				:is-admin="true" />
		</div>
	</div>
</template>

<script lang="ts">
import KeyIcon from 'vue-material-design-icons/Key.vue'
import CloseIcon from 'vue-material-design-icons/Close.vue'
import HelpCircleOutlineIcon from 'vue-material-design-icons/HelpCircleOutline.vue'
import TimerAlertOutlineIcon from 'vue-material-design-icons/TimerAlertOutline.vue'
import ArrowDecisionOutlineIcon from 'vue-material-design-icons/ArrowDecisionOutline.vue'

import PhonetrackIcon from './icons/PhonetrackIcon.vue'

import NcNoteCard from '@nextcloud/vue/components/NcNoteCard'
import NcInputField from '@nextcloud/vue/components/NcInputField'
import NcTextField from '@nextcloud/vue/components/NcTextField'
import NcButton from '@nextcloud/vue/components/NcButton'
import NcFormBoxSwitch from '@nextcloud/vue/components/NcFormBoxSwitch'
import NcFormBox from '@nextcloud/vue/components/NcFormBox'

import TileServerList from './tileservers/TileServerList.vue'

import { loadState } from '@nextcloud/initial-state'
import { generateUrl } from '@nextcloud/router'
import axios from '@nextcloud/axios'
import { delay } from '../utils.js'
import { TileServer } from '../types.ts'
import { subscribe, unsubscribe } from '@nextcloud/event-bus'
import { confirmPassword } from '@nextcloud/password-confirmation'
import { showSuccess, showError } from '@nextcloud/dialogs'
import debounce from 'debounce'

export default {
	name: 'AdminSettings',

	components: {
		PhonetrackIcon,
		TileServerList,
		KeyIcon,
		CloseIcon,
		HelpCircleOutlineIcon,
		TimerAlertOutlineIcon,
		ArrowDecisionOutlineIcon,
		NcNoteCard,
		NcInputField,
		NcTextField,
		NcButton,
		NcFormBoxSwitch,
		NcFormBox,
	},

	props: [],

	data() {
		return {
			state: loadState('phonetrack', 'admin-config', {}) as { maptiler_api_key: string, pointQuota: string, proxy_osm: boolean, extra_tile_servers: Array<TileServer>, [key: string]: any },
			mainHintHtml: t('phonetrack', 'You can create an API key on {maptilerLink}',
				{
					maptilerLink: '<a href="https://cloud.maptiler.com/account/keys/" class="external" target="blank">https://cloud.maptiler.com/account/keys/</a>',
				},
				undefined, { escape: false, sanitize: false }),
		}
	},

	watch: {
	},

	mounted() {
		subscribe('tile-server-deleted', this.onTileServerDeleted as any)
		subscribe('tile-server-added', this.onTileServerAdded as any)
		subscribe('tile-server-edited', this.onTileServerEdited as any)
		console.debug('phonetrack state', this.state)
	},

	unmounted() {
		unsubscribe('tile-server-deleted', this.onTileServerDeleted as any)
		unsubscribe('tile-server-added', this.onTileServerAdded as any)
		unsubscribe('tile-server-edited', this.onTileServerEdited as any)
	},

	methods: {
		onCheckboxChanged(newValue: boolean, key: string): void {
			this.state[key] = newValue
			this.saveOptions({ [key]: this.state[key] ? '1' : '0' }, false)
		},
		onQuotaUpdate: debounce(function(this: any) {
			this.saveOptions({
				pointQuota: parseInt(this.state.pointQuota) || 0,
			}, false)
		}, 2000),
		onInput(): void {
			delay(() => {
				if (this.state.maptiler_api_key === 'dummyApiKey') {
					return
				}
				this.saveOptions({
					maptiler_api_key: this.state.maptiler_api_key,
				}, true)
			}, 2000)()
		},
		async saveOptions(values: Object, sensitive: boolean = true) {
			if (sensitive) {
				await confirmPassword()
			}

			const req = {
				values,
			}
			const url = sensitive
				? generateUrl('/apps/phonetrack/admin-config/sensitive')
				: generateUrl('/apps/phonetrack/admin-config')
			axios.put(url, req).then((response) => {
				showSuccess(t('phonetrack', 'PhoneTrack admin settings saved'))
			}).catch((error) => {
				showError(t('phonetrack', 'Failed to save phonetrack admin settings'))
				console.debug(error)
			})
		},
		onTileServerDeleted(id: number): void {
			const url = generateUrl('/apps/phonetrack/admin/tileservers/{id}', { id })
			axios.delete(url)
				.then((response) => {
					const index = this.state.extra_tile_servers.findIndex(ts => ts.id === id)
					if (index !== -1) {
						this.state.extra_tile_servers.splice(index, 1)
					}
				}).catch((error) => {
					showError(t('phonetrack', 'Failed to delete the tile server'))
					console.debug(error)
				})
		},
		onTileServerAdded(ts: TileServer): void {
			const req = {
				...ts,
			}
			const url = generateUrl('/apps/phonetrack/admin/tileservers')
			axios.post(url, req)
				.then((response) => {
					this.state.extra_tile_servers.push(response.data)
				}).catch((error) => {
					showError(t('phonetrack', 'Failed to add the tile server'))
					console.debug(error)
				})
		},
		onTileServerEdited({ ts, isAdminTileServer }: { ts: TileServer; isAdminTileServer: boolean }): void {
			console.debug('tile server edited', isAdminTileServer, ts)
			const { id: _, ...values } = ts
			const req = {
				...values,
			}
			const url = generateUrl('/apps/phonetrack/admin/tileservers/{id}', { id: ts.id })
			axios.put(url, req)
				.then((response) => {
					const index = this.state.extra_tile_servers.findIndex(item => item.id === ts.id)
					if (index !== -1) {
						Object.assign(this.state.extra_tile_servers[index], values)
					}
				}).catch((error) => {
					showError(t('phonetrack', 'Failed to update the tile server'))
					console.debug(error)
				})
		},
	},
}
</script>

<style scoped lang="scss">
#phonetrack_prefs {
	h2 {
		display: flex;
		justify-content: start;
		gap: 12px;
	}

	#phonetrack-content {
		margin-left: 30px;
		max-width: 800px;
		display: flex;
		flex-direction: column;
		gap: 8px;

		.line {
			display: flex;
			gap: 4px;
			align-items: end;
		}

		.admin-tile-server-list {
			margin-top: 12px;
		}

		.checkbox-inner {
			display: flex;
			gap: 8px;
		}
	}
}
</style>
